import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Modal } from 'Core/components';
import { Printer } from 'react-feather';
import { ProductionService } from '../../service/api/production-service';
import { MachineService } from '../../service/api/machine-service';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';

const MachinePrintModal = ({ showModal, onCloseModal, assetId, cellNumber }) => {
    const dispatch = useDispatch();
    const [selectedPrinter, setSelectedPrinter] = useState(null);
    const [printerOptions, setPrinterOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false);

    useEffect(() => {
        if (showModal) {
            fetchInitialData();
            setSelectedPrinter(null);
        }
    }, [showModal]);

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const printersRes = await ProductionService.getPrinters();

            if (printersRes.status === 200) {
                const printers = printersRes.response || [];
                setPrinterOptions(printers.map(printer => ({
                    value: printer.printerId,
                    label: printer.printerName
                })));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            dispatch(showToast({
                message: 'Failed to load printers',
                type: 'danger',
            }));
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        if (!selectedPrinter || !assetId || !cellNumber) return;

        setIsPrinting(true);
        try {
            const response = await MachineService.printAsset(assetId, cellNumber, selectedPrinter.value);

            if (response.status === 200) {
                dispatch(showToast({
                    message: 'Print request submitted successfully',
                    type: 'success',
                }));
                onCloseModal();
            } else {
                throw new Error(response.message || 'Failed to submit print request');
            }
        } catch (error) {
            console.error("Print error:", error);
            dispatch(showToast({
                message: 'Failed to submit print request',
                type: 'danger',
            }));
        } finally {
            setIsPrinting(false);
        }
    };

    const body = () => (
        <div className="row">
            <div className="col-12 mb-3">
                <label className="form-label">Select Printer</label>
                <Select
                    options={printerOptions}
                    value={selectedPrinter}
                    onChange={setSelectedPrinter}
                    placeholder={loading ? "Loading printers..." : "Select a printer..."}
                    isDisabled={loading || isPrinting}
                />
            </div>
        </div>
    );

    const footer = () => (
        <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-secondary" onClick={onCloseModal} disabled={isPrinting}>
                Cancel
            </button>
            <button
                className="btn btn-primary d-flex align-items-center"
                onClick={handlePrint}
                disabled={!selectedPrinter || loading || isPrinting}
            >
                {isPrinting ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Printing...
                    </>
                ) : (
                    <>
                        <Printer size={16} className="me-2" />
                        Print
                    </>
                )}
            </button>
        </div>
    );

    return (
        <Modal
            id="machine-print-modal"
            show={showModal}
            size="md"
            title="Print Asset Label"
            body={body}
            footer={footer}
            handleClose={onCloseModal}
        />
    );
};

MachinePrintModal.propTypes = {
    showModal: PropTypes.bool,
    onCloseModal: PropTypes.func,
    assetId: PropTypes.string,
    cellNumber: PropTypes.string,
};

export default MachinePrintModal;
